/**
 * Briques communes des échanges avec les élèves, dans l'écran « Messages » :
 * libellés, styles et états (chargement, erreur, rafraîchissement en échec).
 */
import { Loader2 } from 'lucide-react';
import dayjs from '../lib/dates';
import {
  studentMessagingError,
  type ConversationKind,
} from '../api/studentMessaging';

/** Affiché quand le serveur n'a pas répondu ; sinon son message est repris tel quel. */
export const OFFLINE =
  'Connexion impossible. Vérifiez votre réseau, puis réessayez.';

/**
 * Marque d'une conversation où un élève lit. Elle voisine avec les
 * conversations de l'équipe : chaque libellé nomme donc l'élève.
 */
export const KIND_LABELS: Record<ConversationKind, string> = {
  DIRECT: 'Élève',
  GROUP: 'Groupe avec élèves',
  OFFICE: 'Élève et secrétariat',
};

/** Rappel affiché dans le fil : les fonctions de la messagerie de l'équipe n'y existent pas. */
export const TEXT_ONLY: Record<ConversationKind, string> = {
  DIRECT: 'Conversation avec un élève : texte seul.',
  GROUP: 'Conversation avec des élèves : texte seul.',
  OFFICE: 'Conversation avec un élève : texte seul.',
};

// Charte de la messagerie (src/messaging.css), la même que dans l'espace principal.
export const secondaryButton =
  'messaging-button messaging-button--secondary messaging-button--small';

/** Séparateur de jour d'un fil : « Aujourd'hui », « Hier », sinon la date. */
export function dayLabel(iso: string): string {
  const date = dayjs(iso);
  const now = dayjs();
  if (date.isSame(now, 'day')) return 'Aujourd’hui';
  if (date.isSame(now.subtract(1, 'day'), 'day')) return 'Hier';
  return date.format(date.isSame(now, 'year') ? 'D MMMM' : 'D MMMM YYYY');
}

/** Initiales d'un nom, pour sa pastille : deux lettres au plus. */
export function initials(name: string): string {
  return name
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((word) => word[0] ?? '')
    .join('')
    .toUpperCase();
}

export function ErrorState({
  message,
  onRetry,
}: {
  message: string;
  onRetry: () => void;
}) {
  return (
    <div className="p-6 text-center space-y-3">
      <p role="alert" className="messaging-alert">
        {message}
      </p>
      <button type="button" className={secondaryButton} onClick={onRetry}>
        Réessayer
      </button>
    </div>
  );
}

/** Le contenu reste affiché : seul le dernier rafraîchissement a échoué. */
export function RefreshNotice({
  error,
  onRetry,
}: {
  error: unknown;
  onRetry: () => void;
}) {
  return (
    <div role="status" className="messaging-notice">
      <span>{studentMessagingError(error, OFFLINE)}</span>
      <button type="button" onClick={onRetry} className="flex-shrink-0">
        Réessayer
      </button>
    </div>
  );
}

export function Loading({ label }: { label: string }) {
  return (
    <p className="messaging-hint flex items-center justify-center gap-2 p-6">
      <Loader2 size={16} className="animate-spin" />
      {label}
    </p>
  );
}
